import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Pencil, FileText, Send, Trash2, Plus } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-[#F4EFE6] text-[#1E3A8A] border-[#1E3A8A]/30",
  pending_review: "bg-[#FFF8E1] text-[#A87900] border-[#F4C430]/60",
  published: "bg-[#E8F5E9] text-[#1B5E20] border-[#2E7D32]/40",
};

const STATUS_LABEL = {
  draft: "DRAFT",
  pending_review: "AWAITING REVIEW",
  published: "PUBLISHED",
};

const emptyArticle = { title: "", excerpt: "", body: "", cover_image: "", gallery: [], video_url: "" };

export function ContributorArticlesPanel() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/articles/mine");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load articles");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm(emptyArticle);
    setShowForm(false);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      excerpt: item.excerpt || "",
      body: item.body || "",
      cover_image: item.cover_image || "",
      gallery: Array.isArray(item.gallery) ? item.gallery : [],
      video_url: item.video_url || "",
    });
    setShowForm(true);
  };

  // Multi-image upload helpers — used for both cover and gallery via /api/upload.
  const uploadOne = async (file) => {
    const data = new FormData();
    data.append("file", file);
    const { data: r } = await api.post("/upload", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.url;
  };

  const [videoUploading, setVideoUploading] = useState(false);
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: r } = await api.post("/upload/video", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, video_url: r.url }));
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed");
    } finally {
      setVideoUploading(false);
      // Clear input so re-selecting same file fires onChange.
      e.target.value = "";
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadOne(file);
      setForm((f) => ({ ...f, cover_image: url }));
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error("Cover upload failed");
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = await Promise.all(files.map(uploadOne));
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls].slice(0, 20) }));
      toast.success(`${urls.length} photo(s) added`);
    } catch (err) {
      toast.error("One or more uploads failed");
    }
  };

  const removeGalleryAt = (idx) =>
    setForm((f) => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }));

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/articles/${editingId}`, form);
        toast.success("Draft saved");
      } else {
        await api.post("/articles", form);
        toast.success("Draft created");
      }
      reset();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const loadBodyAndEdit = async (id) => {
    try {
      // The /articles/mine list omits the body to keep payload small. Fetch full
      // via admin endpoint isn't open to writers; instead, GET the public slug if
      // published, or fall back to a draft GET via the new admin route.
      // For drafts, the writer can edit but needs the body — fetch from the admin
      // content GET (writers can hit this since auth is checked). Simpler: GET
      // /content/{slug} works for published only. For drafts we fetch the full
      // article via a dedicated writer endpoint.
      const { data } = await api.get(`/articles/${id}`);
      startEdit(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load article");
    }
  };

  const submitForReview = async (id) => {
    if (!window.confirm("Submit this article for editorial review? You won't be able to edit it until an admin approves or returns it.")) return;
    try {
      await api.post(`/articles/${id}/submit`);
      toast.success("Submitted for review");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    }
  };

  return (
    <section data-testid="contributor-articles-panel" className="mt-12">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Editorial</p>
          <h2 className="font-serif-display text-3xl tracking-tight font-medium mt-2 text-[#111]">
            Your articles
          </h2>
        </div>
        {!showForm && (
          <Button
            onClick={() => {
              setForm(emptyArticle);
              setEditingId(null);
              setShowForm(true);
            }}
            data-testid="new-article-btn"
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            New draft
          </Button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={submitForm}
          data-testid="article-form"
          className="mt-6 border border-[#E5E0D8] bg-white p-6 space-y-4"
        >
          <div>
            <label className="label-eyebrow text-[#555]">Title</label>
            <Input
              data-testid="article-title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Excerpt · 1-2 sentence hook</label>
            <Input
              data-testid="article-excerpt"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              maxLength={500}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Cover image</label>
            <div className="mt-2 flex items-center gap-3">
              <input
                data-testid="article-cover-file"
                type="file"
                accept="image/*"
                onChange={handleCoverUpload}
                className="text-xs"
              />
              {form.cover_image && (
                <img src={form.cover_image} alt="" className="h-12 w-20 object-cover border border-[#E5E0D8]" />
              )}
            </div>
            <Input
              data-testid="article-cover"
              value={form.cover_image || ""}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="…or paste a URL"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Photo gallery · up to 20</label>
            <input
              data-testid="article-gallery-file"
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryUpload}
              className="mt-2 text-xs"
            />
            {form.gallery?.length > 0 && (
              <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
                {form.gallery.map((url, idx) => (
                  <div key={`${url}-${idx}`} className="relative group">
                    <img src={url} alt="" className="aspect-square object-cover border border-[#E5E0D8]" />
                    <button
                      type="button"
                      onClick={() => removeGalleryAt(idx)}
                      data-testid={`remove-gallery-${idx}`}
                      className="absolute top-1 right-1 bg-black/60 text-white text-xs px-1.5 opacity-0 group-hover:opacity-100"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Video · upload MP4/WebM/MOV (up to 100MB) or paste embed URL</label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <input
                data-testid="article-video-file"
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                onChange={handleVideoUpload}
                disabled={videoUploading}
                className="text-xs"
              />
              {videoUploading && (
                <span className="label-eyebrow text-[#A87900]">Uploading…</span>
              )}
              {form.video_url && form.video_url.startsWith("/api/files/") && (
                <span className="label-eyebrow text-[#1B5E20]">✓ Hosted video attached</span>
              )}
            </div>
            <Input
              data-testid="article-video"
              value={form.video_url || ""}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder="…or paste YouTube / Vimeo URL"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
            {form.video_url && (
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, video_url: "" }))}
                data-testid="article-video-clear"
                className="mt-2 label-eyebrow text-[#555] hover:text-[#D32F2F]"
              >
                Remove video
              </button>
            )}
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Body · markdown supported</label>
            <Textarea
              data-testid="article-body"
              required
              rows={12}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A] font-mono text-sm"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              disabled={saving}
              data-testid="article-save-btn"
              className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow disabled:opacity-50"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Create draft"}
            </Button>
            <button
              type="button"
              onClick={reset}
              data-testid="article-cancel-btn"
              className="label-eyebrow text-[#555] hover:text-[#111]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-3">
        {items.length === 0 ? (
          <p className="text-sm text-[#555]">No articles yet. Click "New draft" to start one.</p>
        ) : (
          items.map((a) => (
            <div
              key={a.id}
              data-testid={`article-row-${a.id}`}
              className="border border-[#E5E0D8] bg-white p-5 flex items-start justify-between gap-6"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`label-eyebrow px-2 py-0.5 border ${STATUS_STYLE[a.status] || STATUS_STYLE.draft}`}
                  >
                    {STATUS_LABEL[a.status] || "DRAFT"}
                  </span>
                  <h3 className="font-serif-display text-xl tracking-tight text-[#111]">{a.title}</h3>
                </div>
                {a.excerpt && <p className="text-sm text-[#555] mt-2">{a.excerpt}</p>}
                <p className="text-xs text-[#555] mt-2">
                  Updated {new Date(a.updated_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {a.status === "draft" && (
                  <>
                    <button
                      onClick={() => loadBodyAndEdit(a.id)}
                      data-testid={`edit-article-${a.id}`}
                      className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1.5"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => submitForReview(a.id)}
                      data-testid={`submit-article-${a.id}`}
                      className="label-eyebrow text-[#1B5E20] hover:text-[#111] flex items-center gap-1.5"
                    >
                      <Send className="w-3 h-3" /> Submit for review
                    </button>
                  </>
                )}
                {a.status === "pending_review" && (
                  <span className="text-xs text-[#A87900] flex items-center gap-1.5">
                    <FileText className="w-3 h-3" />
                    Awaiting editor
                  </span>
                )}
                {a.status === "published" && (
                  <>
                    <button
                      onClick={() => loadBodyAndEdit(a.id)}
                      data-testid={`edit-article-${a.id}`}
                      className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1.5"
                      title="Amend this published article — changes go live immediately"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <a
                      href={`/journal/${a.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="label-eyebrow text-[#1B5E20] hover:text-[#111]"
                      data-testid={`view-published-${a.id}`}
                    >
                      View live →
                    </a>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

const emptyEvent = {
  title: "",
  description: "",
  cover_image: "",
  start_at: "",
  end_at: "",
  location: "",
  external_url: "",
};

export function ContributorEventsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyEvent);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/events/mine");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load events");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setForm(emptyEvent);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      cover_image: ev.cover_image || "",
      start_at: ev.start_at ? ev.start_at.slice(0, 16) : "",
      end_at: ev.end_at ? ev.end_at.slice(0, 16) : "",
      location: ev.location || "",
      external_url: ev.external_url || "",
    });
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.start_at) {
      toast.error("Title, description and start date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Convert local datetime-input to ISO with timezone
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      };
      if (editingId) {
        await api.patch(`/events/${editingId}`, payload);
        toast.success("Event saved");
      } else {
        await api.post("/events", payload);
        toast.success("Event draft created");
      }
      reset();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async (id) => {
    if (!window.confirm("Submit this event for review? Admins will publish it to the public calendar.")) return;
    try {
      await api.post(`/events/${id}/submit`);
      toast.success("Submitted for review");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    }
  };

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <section data-testid="contributor-events-panel" className="mt-12">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Calendar</p>
          <h2 className="font-serif-display text-3xl tracking-tight font-medium mt-2 text-[#111]">
            Your events
          </h2>
        </div>
        {!showForm && (
          <Button
            onClick={() => {
              setForm(emptyEvent);
              setEditingId(null);
              setShowForm(true);
            }}
            data-testid="new-event-btn"
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            New event
          </Button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={submitForm}
          data-testid="event-form"
          className="mt-6 border border-[#E5E0D8] bg-white p-6 space-y-4"
        >
          <div>
            <label className="label-eyebrow text-[#555]">Title</label>
            <Input
              data-testid="event-title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Description</label>
            <Textarea
              data-testid="event-description"
              required
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow text-[#555]">Starts</label>
              <Input
                data-testid="event-start"
                required
                type="datetime-local"
                value={form.start_at}
                onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
              />
            </div>
            <div>
              <label className="label-eyebrow text-[#555]">Ends (optional)</label>
              <Input
                data-testid="event-end"
                type="datetime-local"
                value={form.end_at}
                onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow text-[#555]">Location</label>
              <Input
                data-testid="event-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Lagos, Nigeria"
                className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
              />
            </div>
            <div>
              <label className="label-eyebrow text-[#555]">RSVP / Info URL</label>
              <Input
                data-testid="event-url"
                value={form.external_url}
                onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                placeholder="https://…"
                className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Cover image URL (optional)</label>
            <Input
              data-testid="event-cover"
              value={form.cover_image || ""}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="https://…"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              disabled={saving}
              data-testid="event-save-btn"
              className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow disabled:opacity-50"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Create draft"}
            </Button>
            <button
              type="button"
              onClick={reset}
              data-testid="event-cancel-btn"
              className="label-eyebrow text-[#555] hover:text-[#111]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-3">
        {items.length === 0 ? (
          <p className="text-sm text-[#555]">No events yet. Click "New event" to add one.</p>
        ) : (
          items.map((ev) => (
            <div
              key={ev.id}
              data-testid={`event-row-${ev.id}`}
              className="border border-[#E5E0D8] bg-white p-5 flex items-start justify-between gap-6"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`label-eyebrow px-2 py-0.5 border ${STATUS_STYLE[ev.status] || STATUS_STYLE.draft}`}
                  >
                    {STATUS_LABEL[ev.status] || "DRAFT"}
                  </span>
                  <h3 className="font-serif-display text-xl tracking-tight text-[#111]">{ev.title}</h3>
                </div>
                <p className="text-sm text-[#555] mt-2">
                  {new Date(ev.start_at).toLocaleString()}
                  {ev.location ? ` · ${ev.location}` : ""}
                </p>
                <p className="text-sm text-[#333] mt-2 line-clamp-2">{ev.description}</p>
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {ev.status === "draft" && (
                  <>
                    <button
                      onClick={() => startEdit(ev)}
                      data-testid={`edit-event-${ev.id}`}
                      className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1.5"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => submitForReview(ev.id)}
                      data-testid={`submit-event-${ev.id}`}
                      className="label-eyebrow text-[#1B5E20] hover:text-[#111] flex items-center gap-1.5"
                    >
                      <Send className="w-3 h-3" /> Submit for review
                    </button>
                    <button
                      onClick={() => removeEvent(ev.id)}
                      data-testid={`delete-event-${ev.id}`}
                      className="label-eyebrow text-[#555] hover:text-[#D32F2F] flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </>
                )}
                {ev.status === "pending_review" && (
                  <span className="text-xs text-[#A87900]">Awaiting editor</span>
                )}
                {ev.status === "published" && (
                  <>
                    <button
                      onClick={() => startEdit(ev)}
                      data-testid={`edit-event-${ev.id}`}
                      className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1.5"
                      title="Amend this published event — changes go live immediately"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <a
                      href="/events"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="label-eyebrow text-[#1B5E20] hover:text-[#111]"
                    >
                      Live on calendar →
                    </a>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
